import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppealDto,
  CommitteeCandidateDto,
  DecideAppealDto,
  FileAppealsDto,
  RecordAppealDto,
  RecordHearingDto,
  ScheduleHearingDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';

export type FileAppeals = FileAppealsDto & { canManage: boolean; canDecide: boolean };

export const appealKeys = {
  forFile: (fileId: string) => ['appeals', fileId] as const,
  candidates: (fileId: string) => ['appeals', fileId, 'candidates'] as const,
};

export function useAppeals(fileId: string) {
  return useQuery({
    queryKey: appealKeys.forFile(fileId),
    queryFn: async () => {
      const { data } = await api.get<FileAppeals>(`/files/${fileId}/appeals`);
      return data;
    },
    enabled: !!fileId,
  });
}

export function useCommitteeCandidates(fileId: string, enabled: boolean) {
  return useQuery({
    queryKey: appealKeys.candidates(fileId),
    queryFn: async () => {
      const { data } = await api.get<CommitteeCandidateDto[]>(
        `/files/${fileId}/appeals/committee-candidates`,
      );
      return data;
    },
    enabled: !!fileId && enabled,
  });
}

/**
 * Every appeal write answers with the appeal; the list, the file (whose Step 6
 * the write may have moved) and the deadlines (whose clocks it may have
 * started) are all stale after one.
 */
function useAppealMutation<V>(
  fileId: string,
  request: (vars: V) => Promise<AppealDto>,
  successKey: string,
  errorKey: string,
) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: appealKeys.forFile(fileId) });
      qc.invalidateQueries({ queryKey: ['files'] });
      qc.invalidateQueries({ queryKey: ['deadlines'] });
      toast.success(successKey);
    },
    onError: (err) => toast.error(err, errorKey),
  });
}

const post = async <T>(url: string, body?: T) => (await api.post<AppealDto>(url, body)).data;

export function useRecordAppeal(fileId: string) {
  return useAppealMutation(
    fileId,
    (dto: RecordAppealDto) => post(`/files/${fileId}/appeals`, dto),
    'gov.appeals.recordedToast',
    'gov.appeals.saveError',
  );
}

export function useAcceptAppeal(fileId: string) {
  return useAppealMutation(
    fileId,
    (appealId: string) => post(`/files/${fileId}/appeals/${appealId}/accept`),
    'gov.appeals.acceptedToast',
    'gov.appeals.saveError',
  );
}

export function useRejectAppeal(fileId: string) {
  return useAppealMutation(
    fileId,
    ({ appealId, reason }: { appealId: string; reason: string }) =>
      post(`/files/${fileId}/appeals/${appealId}/reject`, { reason }),
    'gov.appeals.rejectedToast',
    'gov.appeals.saveError',
  );
}

export function useWithdrawAppeal(fileId: string) {
  return useAppealMutation(
    fileId,
    (appealId: string) => post(`/files/${fileId}/appeals/${appealId}/withdraw`, {}),
    'gov.appeals.withdrawnToast',
    'gov.appeals.saveError',
  );
}

export function useScheduleHearing(fileId: string) {
  return useAppealMutation(
    fileId,
    ({ appealId, ...dto }: ScheduleHearingDto & { appealId: string }) =>
      post(`/files/${fileId}/appeals/${appealId}/hearings`, dto),
    'gov.appeals.scheduledToast',
    'gov.appeals.saveError',
  );
}

export function useRecordHearing(fileId: string) {
  return useAppealMutation(
    fileId,
    ({ appealId, hearingId, ...dto }: RecordHearingDto & { appealId: string; hearingId: string }) =>
      post(`/files/${fileId}/appeals/${appealId}/hearings/${hearingId}/record`, dto),
    'gov.appeals.hearingRecordedToast',
    'gov.appeals.saveError',
  );
}

export function useDecideAppeal(fileId: string) {
  return useAppealMutation(
    fileId,
    ({ appealId, ...dto }: DecideAppealDto & { appealId: string }) =>
      post(`/files/${fileId}/appeals/${appealId}/decision`, dto),
    'gov.appeals.decidedToast',
    'gov.appeals.saveError',
  );
}
